import JSZip from 'jszip';

export const readFileAsText = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => resolve(event.target?.result as string);
      reader.onerror = (error) => reject(error);
      reader.readAsText(file);
    }
);

const readFileAsArrayBuffer = (file: File): Promise<ArrayBuffer> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => resolve(event.target?.result as ArrayBuffer);
      reader.onerror = (error) => reject(error);
      reader.readAsArrayBuffer(file);
    });

// ---------- PDF ----------
//
// Extract plain text from a PDF in the browser using pdf.js. This matches
// roughly what the server will extract, so the token count we derive from it
// is realistic instead of tokenizing raw binary. We import `pdfjs-dist`
// directly (rather than via `react-pdf`) so this works from non-React call
// sites too, and point the worker at the same CDN URL PDFViewer uses.

let pdfjsModulePromise: Promise<typeof import('pdfjs-dist')> | null = null;

async function loadPdfjs(): Promise<typeof import('pdfjs-dist')> {
  if (!pdfjsModulePromise) {
    pdfjsModulePromise = import('pdfjs-dist').then((mod) => {
      try {
        if (!mod.GlobalWorkerOptions.workerSrc) {
          mod.GlobalWorkerOptions.workerSrc =
            `https://unpkg.com/pdfjs-dist@${mod.version}/build/pdf.worker.min.mjs`;
        }
      } catch {
        // Ignore: extraction will fail downstream and we fall back to '' there.
      }
      return mod;
    });
  }
  return pdfjsModulePromise;
}

export async function readPdfFile(file: File): Promise<string> {
  const pdfjs = await loadPdfjs();
  const data = await readFileAsArrayBuffer(file);

  let pdf: Awaited<ReturnType<typeof pdfjs.getDocument>['promise']> | null = null;
  try {
    pdf = await pdfjs.getDocument({ data }).promise;
    const pageTexts: string[] = [];
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const textContent = await page.getTextContent();
      const pageText = textContent.items
        .map((item) => ('str' in item ? (item as { str: string }).str : ''))
        .join(' ');
      pageTexts.push(pageText);
      page.cleanup();
    }
    return pageTexts.join('\n\n').trim();
  } catch {
    return '';
  } finally {
    if (pdf) {
      try { await pdf.destroy(); } catch { /* noop */ }
    }
  }
}

// ---------- DOCX ----------

export interface DocxParagraph {
  /** Already-concatenated text of the paragraph (no leading bullet marker). */
  text: string;
  /** True when w:numPr is present on the paragraph's w:pPr (list item). */
  isList: boolean;
  /** Heading level 1-6 if the paragraph uses a Heading style, else undefined. */
  headingLevel?: number;
}

export interface DocxContent {
  paragraphs: DocxParagraph[];
}

/**
 * Parses a .docx (OpenXML WordprocessingML) ArrayBuffer into a structured
 * paragraph list. Paragraph style (`w:pStyle` val like "Heading1") is
 * preserved when possible so the viewer can render headings, lists, etc.
 */
export async function parseDocxBuffer(arrayBuffer: ArrayBuffer): Promise<DocxContent> {
  const zip = await JSZip.loadAsync(arrayBuffer);
  const docXml = await zip.file('word/document.xml')?.async('string');
  if (!docXml) throw new Error('DOCX file missing document.xml');

  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(docXml, 'application/xml');
  const wNs = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
  const paragraphEls = xmlDoc.getElementsByTagNameNS(wNs, 'p');

  const paragraphs: DocxParagraph[] = [];
  for (let i = 0; i < paragraphEls.length; i++) {
    const paragraph = paragraphEls[i];
    const pPr = paragraph.getElementsByTagNameNS(wNs, 'pPr')[0];
    const isList = !!pPr?.getElementsByTagNameNS(wNs, 'numPr').length;

    let headingLevel: number | undefined;
    const pStyle = pPr?.getElementsByTagNameNS(wNs, 'pStyle')[0];
    const styleVal = pStyle?.getAttributeNS(wNs, 'val') || pStyle?.getAttribute('w:val') || '';
    const headingMatch = styleVal.match(/^Heading(\d)$/i);
    if (headingMatch) headingLevel = Math.min(6, parseInt(headingMatch[1], 10));

    const runs = paragraph.getElementsByTagNameNS(wNs, 'r');
    let text = '';
    for (let j = 0; j < runs.length; j++) {
      const children = Array.from(runs[j].childNodes) as Element[];
      for (const child of children) {
        if (child.nodeType !== 1) continue;
        const local = (child as Element).localName;
        if (local === 't') {
          text += child.textContent || '';
        } else if (local === 'br') {
          text += '\n';
        } else if (local === 'tab') {
          text += '\t';
        }
      }
    }
    paragraphs.push({ text, isList, headingLevel });
  }
  return { paragraphs };
}

/** Back-compat: flatten a docx file to a single string (used by the send path). */
export const readDocxFile = async (file: File): Promise<string> => {
  try {
    const { paragraphs } = await parseDocxBuffer(await readFileAsArrayBuffer(file));
    return paragraphs
      .map((p) => (p.isList ? `- ${p.text}` : p.text))
      .join('\n');
  } catch (error: any) {
    throw new Error(
      error?.message
        ? `${error.message} (file: ${file.name}, size: ${file.size})`
        : `Failed to read DOCX (file: ${file.name})`
    );
  }
};

// ---------- PPTX ----------

export interface PptxSlide {
  /** 1-based slide index. */
  index: number;
  /** Heuristically-detected slide title (first non-empty paragraph). */
  title?: string;
  /** All paragraphs on the slide in document order (incl. title). */
  paragraphs: string[];
}

/**
 * Parses a .pptx (OpenXML PresentationML) ArrayBuffer into a list of slides.
 * Each slide carries its raw paragraphs so the viewer can present them as a
 * deck of cards with navigation.
 */
export async function parsePptxBuffer(arrayBuffer: ArrayBuffer): Promise<PptxSlide[]> {
  const zip = await JSZip.loadAsync(arrayBuffer);
  const slideEntries = Object.keys(zip.files)
    .filter((name) => /^ppt\/slides\/slide\d+\.xml$/i.test(name))
    .sort((a, b) => {
      const ai = parseInt(a.match(/slide(\d+)\.xml$/i)?.[1] ?? '0', 10);
      const bi = parseInt(b.match(/slide(\d+)\.xml$/i)?.[1] ?? '0', 10);
      return ai - bi;
    });
  if (slideEntries.length === 0) throw new Error('PPTX file has no slides');

  const parser = new DOMParser();
  const aNs = 'http://schemas.openxmlformats.org/drawingml/2006/main';
  const slides: PptxSlide[] = [];

  for (let i = 0; i < slideEntries.length; i++) {
    const slideXml = await zip.file(slideEntries[i])?.async('string');
    if (!slideXml) continue;

    const xmlDoc = parser.parseFromString(slideXml, 'application/xml');
    const paragraphEls = xmlDoc.getElementsByTagNameNS(aNs, 'p');
    const paragraphs: string[] = [];

    for (let p = 0; p < paragraphEls.length; p++) {
      const paragraph = paragraphEls[p];
      const runs = paragraph.getElementsByTagNameNS(aNs, 'r');
      let line = '';
      for (let r = 0; r < runs.length; r++) {
        const tEls = runs[r].getElementsByTagNameNS(aNs, 't');
        for (let t = 0; t < tEls.length; t++) {
          line += tEls[t].textContent || '';
        }
      }
      const brs = paragraph.getElementsByTagNameNS(aNs, 'br');
      if (line || brs.length > 0) {
        paragraphs.push(line);
      }
    }

    const title = paragraphs.find((l) => l.trim().length > 0);
    slides.push({ index: i + 1, title, paragraphs });
  }
  return slides;
}

// ---------- PPTX (rich layout) ----------

/**
 * EMU = English Metric Unit. 914400 EMU = 1 inch. At 96 DPI, 1 px = 9525 EMU.
 * We normalize to px so the viewer can render naturally and scale via CSS
 * transform to fit any container.
 */
export const EMU_PER_PX = 9525;

export interface PptxTextRun {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  /** Point size if explicit (or inherited default injected during parsing). */
  fontSizePt?: number;
  /** Resolved color (`#rrggbb`) when available. */
  color?: string;
  fontFamily?: string;
}

export interface PptxParagraph {
  runs: PptxTextRun[];
  /** Indent level 0-8 (from `a:pPr lvl`). */
  level: number;
  bullet: boolean;
  bulletChar?: string;
  align: 'left' | 'center' | 'right' | 'justify';
}

export interface PptxRect {
  /** Natural pixel coordinates at 96 DPI. */
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PptxPadding {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export type PptxShape =
  | {
      kind: 'text';
      rect: PptxRect;
      paragraphs: PptxParagraph[];
      fill?: string;
      defaultFontSizePt: number;
      defaultTextColor?: string;
      defaultFontFamily?: string;
      padding: PptxPadding;
      /** Vertical alignment inside the shape (`bodyPr anchor`). */
      verticalAlign: 'top' | 'middle' | 'bottom';
    }
  | {
      kind: 'image';
      rect: PptxRect;
      src: string;
    };

export interface PptxSlideLayout {
  index: number;
  shapes: PptxShape[];
  background?: string;
}

export interface PptxDeck {
  /** Slide width in natural pixels (EMU ÷ 9525). */
  width: number;
  /** Slide height in natural pixels. */
  height: number;
  slides: PptxSlideLayout[];
  /** Object URLs for embedded images — caller should revoke on teardown. */
  blobUrls: string[];
}

const A_NS = 'http://schemas.openxmlformats.org/drawingml/2006/main';
const P_NS = 'http://schemas.openxmlformats.org/presentationml/2006/main';
const R_NS = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';
const REL_NS = 'http://schemas.openxmlformats.org/package/2006/relationships';

const DEFAULT_CLR_MAP: Record<string, string> = {
  bg1: 'lt1',
  tx1: 'dk1',
  bg2: 'lt2',
  tx2: 'dk2',
  accent1: 'accent1',
  accent2: 'accent2',
  accent3: 'accent3',
  accent4: 'accent4',
  accent5: 'accent5',
  accent6: 'accent6',
  hlink: 'hlink',
  folHlink: 'folHlink',
};

const DEFAULT_THEME_COLORS: Record<string, string> = {
  dk1: '#000000',
  lt1: '#ffffff',
  dk2: '#1f497d',
  lt2: '#eeece1',
  accent1: '#4f81bd',
  accent2: '#c0504d',
  accent3: '#9bbb59',
  accent4: '#8064a2',
  accent5: '#4bacc6',
  accent6: '#f79646',
  hlink: '#0000ff',
  folHlink: '#800080',
};

interface ThemeData {
  colors: Record<string, string>;
  majorLatin?: string;
  minorLatin?: string;
}

interface RelEntry {
  target: string;
  type: string;
}

interface ParagraphDefault {
  align?: PptxParagraph['align'];
  bullet?: boolean;
  bulletChar?: string;
  run: Partial<Omit<PptxTextRun, 'text'>>;
}

interface MasterTextStyles {
  title: ParagraphDefault[];
  body: ParagraphDefault[];
  other: ParagraphDefault[];
}

interface PlaceholderInfo {
  type: string;
  idx: string;
  key: string;
}

interface PlaceholderDefaults {
  rect?: PptxRect;
  fill?: string;
  padding?: PptxPadding;
  verticalAlign?: 'top' | 'middle' | 'bottom';
}

interface LayoutData {
  masterPath?: string;
  placeholders: Record<string, PlaceholderDefaults>;
  background?: string;
}

interface MasterData {
  clrMap: Record<string, string>;
  textStyles: MasterTextStyles;
  placeholders: Record<string, PlaceholderDefaults>;
  background?: string;
}

interface ParseCtx {
  theme: ThemeData;
  clrMap: Record<string, string>;
}

const EMPTY_TEXT_STYLES: MasterTextStyles = {
  title: [],
  body: [],
  other: [],
};

function directChild(el: Element | null | undefined, local: string, ns?: string): Element | null {
  if (!el) return null;
  for (const c of Array.from(el.children)) {
    if (c.localName === local && (!ns || c.namespaceURI === ns)) return c;
  }
  return null;
}

function emuAttr(el: Element | null, name: string): number {
  if (!el) return 0;
  return parseInt(el.getAttribute(name) || '0', 10) || 0;
}

function parseXfrmRect(spPr: Element | null): PptxRect | null {
  const xfrm = directChild(spPr, 'xfrm', A_NS);
  const off = directChild(xfrm, 'off', A_NS);
  const ext = directChild(xfrm, 'ext', A_NS);
  if (!off || !ext) return null;
  return {
    x: emuAttr(off, 'x') / EMU_PER_PX,
    y: emuAttr(off, 'y') / EMU_PER_PX,
    width: emuAttr(ext, 'cx') / EMU_PER_PX,
    height: emuAttr(ext, 'cy') / EMU_PER_PX,
  };
}

function parseRelationships(xml: string): Record<string, RelEntry> {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xml, 'application/xml');
  const rels = doc.getElementsByTagNameNS(REL_NS, 'Relationship');
  const out: Record<string, RelEntry> = {};
  for (let i = 0; i < rels.length; i++) {
    const id = rels[i].getAttribute('Id') || '';
    const target = rels[i].getAttribute('Target') || '';
    const type = rels[i].getAttribute('Type') || '';
    if (id && target) out[id] = { target, type };
  }
  return out;
}

function resolveRelTarget(ownerPath: string, target: string): string {
  const ownerDir = ownerPath.replace(/\/[^/]+$/, '');
  const parts = ownerDir.split('/');
  for (const segment of target.split('/')) {
    if (!segment || segment === '.') continue;
    if (segment === '..') {
      parts.pop();
    } else {
      parts.push(segment);
    }
  }
  return parts.join('/');
}

function clampChannel(v: number): number {
  return Math.max(0, Math.min(255, Math.round(v)));
}

function hexToRgb(hex: string): [number, number, number] | null {
  const m = hex.match(/^#?([0-9a-f]{6})$/i);
  if (!m) return null;
  const value = m[1];
  return [
    parseInt(value.slice(0, 2), 16),
    parseInt(value.slice(2, 4), 16),
    parseInt(value.slice(4, 6), 16),
  ];
}

function rgbToHex([r, g, b]: [number, number, number]): string {
  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(clampChannel(r))}${toHex(clampChannel(g))}${toHex(clampChannel(b))}`;
}

function applyColorModifiers(baseHex: string, colorEl: Element): string {
  const rgb = hexToRgb(baseHex);
  if (!rgb) return baseHex;
  let [r, g, b] = rgb;
  for (const child of Array.from(colorEl.children)) {
    if (child.namespaceURI !== A_NS) continue;
    const valRaw = parseInt(child.getAttribute('val') || '0', 10);
    const val = Number.isFinite(valRaw) ? Math.max(0, Math.min(100000, valRaw)) / 100000 : 0;
    if (child.localName === 'tint') {
      r = r + (255 - r) * val;
      g = g + (255 - g) * val;
      b = b + (255 - b) * val;
    } else if (child.localName === 'shade') {
      r = r * val;
      g = g * val;
      b = b * val;
    } else if (child.localName === 'lumMod') {
      r = r * val;
      g = g * val;
      b = b * val;
    } else if (child.localName === 'lumOff') {
      r = r + 255 * val;
      g = g + 255 * val;
      b = b + 255 * val;
    }
  }
  return rgbToHex([r, g, b]);
}

function resolveColorNode(
  colorNode: Element | null,
  theme: ThemeData,
  clrMap: Record<string, string>
): string | undefined {
  if (!colorNode) return undefined;
  if (colorNode.namespaceURI !== A_NS) return undefined;

  let base: string | undefined;
  if (colorNode.localName === 'srgbClr') {
    const val = colorNode.getAttribute('val');
    if (val) base = `#${val}`;
  } else if (colorNode.localName === 'sysClr') {
    const val = colorNode.getAttribute('lastClr') || colorNode.getAttribute('val');
    if (val) base = `#${val}`;
  } else if (colorNode.localName === 'schemeClr') {
    const key = colorNode.getAttribute('val') || '';
    const mapped = clrMap[key] || key;
    base = theme.colors[mapped] || theme.colors[key];
  }

  if (!base) return undefined;
  return applyColorModifiers(base, colorNode);
}

function firstColorChild(parent: Element | null | undefined): Element | null {
  if (!parent) return null;
  for (const child of Array.from(parent.children)) {
    if (child.namespaceURI !== A_NS) continue;
    if (['srgbClr', 'schemeClr', 'sysClr'].includes(child.localName)) return child;
  }
  return null;
}

function parseSolidFillColor(
  parent: Element | null | undefined,
  theme: ThemeData,
  clrMap: Record<string, string>
): string | undefined {
  if (!parent) return undefined;
  const solid = directChild(parent, 'solidFill', A_NS);
  const color = resolveColorNode(firstColorChild(solid), theme, clrMap);
  if (color) return color;

  // Some shape styles point to a fillRef/fontRef with a direct scheme color.
  const refColor = resolveColorNode(firstColorChild(parent), theme, clrMap);
  if (refColor) return refColor;
  return undefined;
}

function resolveTypeface(typeface: string | null, theme: ThemeData): string | undefined {
  if (!typeface) return undefined;
  if (typeface.startsWith('+mj')) return theme.majorLatin || undefined;
  if (typeface.startsWith('+mn')) return theme.minorLatin || undefined;
  return typeface;
}

function parseRunFormat(
  rPr: Element | null,
  theme: ThemeData,
  clrMap: Record<string, string>
): Partial<PptxTextRun> {
  if (!rPr) return {};
  const out: Partial<PptxTextRun> = {};

  const b = rPr.getAttribute('b');
  if (b !== null) out.bold = b === '1';
  const i = rPr.getAttribute('i');
  if (i !== null) out.italic = i === '1';
  const u = rPr.getAttribute('u');
  if (u !== null) out.underline = u !== 'none';

  const sz = rPr.getAttribute('sz');
  if (sz) {
    const v = parseInt(sz, 10);
    if (Number.isFinite(v)) out.fontSizePt = v / 100;
  }

  const color = parseSolidFillColor(rPr, theme, clrMap);
  if (color) out.color = color;

  const latin = directChild(rPr, 'latin', A_NS);
  const typeface = resolveTypeface(latin?.getAttribute('typeface') || null, theme);
  if (typeface) out.fontFamily = typeface;
  return out;
}

function parseAlign(el: Element | null): PptxParagraph['align'] | undefined {
  const algnAttr = el?.getAttribute('algn');
  if (algnAttr === 'ctr') return 'center';
  if (algnAttr === 'r') return 'right';
  if (algnAttr === 'just') return 'justify';
  if (algnAttr === 'l') return 'left';
  return undefined;
}

function parseParagraphDefault(
  pPr: Element,
  theme: ThemeData,
  clrMap: Record<string, string>
): ParagraphDefault {
  const buNone = !!directChild(pPr, 'buNone', A_NS);
  const buCharEl = directChild(pPr, 'buChar', A_NS);
  const buAutoNum = !!directChild(pPr, 'buAutoNum', A_NS);
  const bullet = buNone ? false : (buCharEl ? true : buAutoNum ? true : undefined);
  const bulletChar = buCharEl?.getAttribute('char') || undefined;
  const defRPr = directChild(pPr, 'defRPr', A_NS);
  return {
    align: parseAlign(pPr),
    bullet,
    bulletChar,
    run: parseRunFormat(defRPr, theme, clrMap),
  };
}

function parseLevelDefaults(
  parent: Element | null,
  theme: ThemeData,
  clrMap: Record<string, string>
): ParagraphDefault[] {
  const levels: ParagraphDefault[] = [];
  if (!parent) return levels;
  for (const child of Array.from(parent.children)) {
    if (child.namespaceURI !== A_NS) continue;
    const m = child.localName.match(/^lvl([1-9])pPr$/);
    if (!m) continue;
    const idx = parseInt(m[1], 10) - 1;
    levels[idx] = parseParagraphDefault(child, theme, clrMap);
  }
  return levels;
}

function parseMasterClrMap(masterDoc: Document): Record<string, string> {
  const clrMapEl = masterDoc.getElementsByTagNameNS(P_NS, 'clrMap')[0];
  const out = { ...DEFAULT_CLR_MAP };
  if (!clrMapEl) return out;
  for (const k of Object.keys(DEFAULT_CLR_MAP)) {
    const v = clrMapEl.getAttribute(k);
    if (v) out[k] = v;
  }
  return out;
}

function parseMasterTextStyles(masterDoc: Document, ctx: ParseCtx): MasterTextStyles {
  const txStyles = masterDoc.getElementsByTagNameNS(P_NS, 'txStyles')[0];
  if (!txStyles) return EMPTY_TEXT_STYLES;
  const titleStyle = directChild(txStyles, 'titleStyle', P_NS);
  const bodyStyle = directChild(txStyles, 'bodyStyle', P_NS);
  const otherStyle = directChild(txStyles, 'otherStyle', P_NS);
  return {
    title: parseLevelDefaults(titleStyle, ctx.theme, ctx.clrMap),
    body: parseLevelDefaults(bodyStyle, ctx.theme, ctx.clrMap),
    other: parseLevelDefaults(otherStyle, ctx.theme, ctx.clrMap),
  };
}

function getPlaceholderInfo(sp: Element): PlaceholderInfo | null {
  const nvSpPr = directChild(sp, 'nvSpPr', P_NS);
  const nvPr = directChild(nvSpPr, 'nvPr', P_NS);
  const ph = directChild(nvPr, 'ph', P_NS);
  if (!ph) return null;
  const type = ph.getAttribute('type') || 'body';
  const idx = ph.getAttribute('idx') || '0';
  return { type, idx, key: `${type}:${idx}` };
}

function defaultFontSizeForPh(ph: string | null): number {
  switch (ph) {
    case 'title':
    case 'ctrTitle':
      return 40;
    case 'subTitle':
      return 28;
    case 'body':
      return 22;
    case 'sldNum':
    case 'ftr':
    case 'dt':
      return 12;
    default:
      return 18;
  }
}

function bucketForPlaceholder(type: string | null): keyof MasterTextStyles {
  if (!type) return 'other';
  if (type === 'title' || type === 'ctrTitle' || type === 'subTitle') return 'title';
  if (type === 'body' || type === 'obj') return 'body';
  return 'other';
}

function parseBodyPadding(bodyPr: Element | null): PptxPadding | undefined {
  if (!bodyPr) return undefined;
  const hasAny =
    bodyPr.hasAttribute('lIns') ||
    bodyPr.hasAttribute('rIns') ||
    bodyPr.hasAttribute('tIns') ||
    bodyPr.hasAttribute('bIns');
  if (!hasAny) return undefined;
  return {
    left: emuAttr(bodyPr, 'lIns') / EMU_PER_PX,
    right: emuAttr(bodyPr, 'rIns') / EMU_PER_PX,
    top: emuAttr(bodyPr, 'tIns') / EMU_PER_PX,
    bottom: emuAttr(bodyPr, 'bIns') / EMU_PER_PX,
  };
}

function parseVerticalAlign(bodyPr: Element | null): 'top' | 'middle' | 'bottom' | undefined {
  const anchor = bodyPr?.getAttribute('anchor');
  if (anchor === 'ctr') return 'middle';
  if (anchor === 'b') return 'bottom';
  if (anchor === 't') return 'top';
  return undefined;
}

function parseShapeFill(
  node: Element,
  spPr: Element | null,
  theme: ThemeData,
  clrMap: Record<string, string>
): string | undefined {
  const explicit = parseSolidFillColor(spPr, theme, clrMap);
  if (explicit) return explicit;
  const style = directChild(node, 'style', P_NS);
  const fillRef = directChild(style, 'fillRef', A_NS);
  return parseSolidFillColor(fillRef, theme, clrMap);
}

function parseBackgroundColor(
  cSld: Element | null,
  theme: ThemeData,
  clrMap: Record<string, string>
): string | undefined {
  const bg = directChild(cSld, 'bg', P_NS);
  const bgPr = directChild(bg, 'bgPr', P_NS);
  const fromPr = parseSolidFillColor(bgPr, theme, clrMap);
  if (fromPr) return fromPr;
  const bgRef = directChild(bg, 'bgRef', P_NS);
  return resolveColorNode(firstColorChild(bgRef), theme, clrMap);
}

function parsePlaceholderDefaults(
  cSld: Element | null,
  theme: ThemeData,
  clrMap: Record<string, string>
): Record<string, PlaceholderDefaults> {
  const out: Record<string, PlaceholderDefaults> = {};
  const spTree = cSld ? directChild(cSld, 'spTree', P_NS) : null;
  if (!spTree) return out;
  for (const node of Array.from(spTree.children)) {
    if (node.namespaceURI !== P_NS || node.localName !== 'sp') continue;
    const ph = getPlaceholderInfo(node);
    if (!ph) continue;
    const spPr = directChild(node, 'spPr', P_NS);
    const txBody = directChild(node, 'txBody', P_NS);
    const bodyPr = directChild(txBody, 'bodyPr', A_NS);
    out[ph.key] = {
      rect: parseXfrmRect(spPr) || undefined,
      fill: parseShapeFill(node, spPr, theme, clrMap),
      padding: parseBodyPadding(bodyPr),
      verticalAlign: parseVerticalAlign(bodyPr),
    };
  }
  return out;
}

function mergePlaceholderDefaults(
  primary: PlaceholderDefaults | undefined,
  secondary: PlaceholderDefaults | undefined
): PlaceholderDefaults {
  return {
    rect: primary?.rect || secondary?.rect,
    fill: primary?.fill ?? secondary?.fill,
    padding: primary?.padding || secondary?.padding,
    verticalAlign: primary?.verticalAlign || secondary?.verticalAlign,
  };
}

async function parseThemeDataAsync(zip: JSZip, parser: DOMParser): Promise<ThemeData> {
  const themePath =
    Object.keys(zip.files).find((p) => /^ppt\/theme\/theme\d+\.xml$/i.test(p)) ||
    'ppt/theme/theme1.xml';
  const themeXml = await zip.file(themePath)?.async('string');
  if (!themeXml) return { colors: { ...DEFAULT_THEME_COLORS } };

  const doc = parser.parseFromString(themeXml, 'application/xml');
  const colors = { ...DEFAULT_THEME_COLORS };
  const clrScheme = doc.getElementsByTagNameNS(A_NS, 'clrScheme')[0];
  if (clrScheme) {
    for (const child of Array.from(clrScheme.children)) {
      if (child.namespaceURI !== A_NS) continue;
      const name = child.localName;
      const srgb = directChild(child, 'srgbClr', A_NS)?.getAttribute('val');
      const sys = directChild(child, 'sysClr', A_NS);
      const sysVal = sys?.getAttribute('lastClr') || sys?.getAttribute('val');
      const resolved = srgb || sysVal;
      if (resolved) colors[name] = `#${resolved}`;
    }
  }

  // Read major/minor latin directly from fontScheme.
  const majorFont = doc.getElementsByTagNameNS(A_NS, 'majorFont')[0];
  const minorFont = doc.getElementsByTagNameNS(A_NS, 'minorFont')[0];
  const majorTypeface = directChild(majorFont, 'latin', A_NS)?.getAttribute('typeface') || undefined;
  const minorTypeface = directChild(minorFont, 'latin', A_NS)?.getAttribute('typeface') || undefined;

  return {
    colors,
    majorLatin: majorTypeface || undefined,
    minorLatin: minorTypeface || undefined,
  };
}

function pickParagraphDefaults(
  level: number,
  paragraphDefaults: ParagraphDefault[],
  masterDefaults: ParagraphDefault[]
): ParagraphDefault | undefined {
  return paragraphDefaults[level] || masterDefaults[level] || masterDefaults[0];
}

function parseParagraphs(
  txBody: Element,
  placeholderType: string | null,
  masterStyles: MasterTextStyles,
  theme: ThemeData,
  clrMap: Record<string, string>
): PptxParagraph[] {
  const out: PptxParagraph[] = [];
  const listStyle = directChild(txBody, 'lstStyle', A_NS);
  const shapeDefaults = parseLevelDefaults(listStyle, theme, clrMap);
  const bucket = masterStyles[bucketForPlaceholder(placeholderType)] || [];

  for (const p of Array.from(txBody.children)) {
    if (p.namespaceURI !== A_NS || p.localName !== 'p') continue;
    const pPr = directChild(p, 'pPr', A_NS);
    const level = parseInt(pPr?.getAttribute('lvl') || '0', 10) || 0;
    const inherited = pickParagraphDefaults(level, shapeDefaults, bucket);

    const explicitAlign = parseAlign(pPr);
    const align = explicitAlign || inherited?.align || 'left';

    const buNone = !!directChild(pPr, 'buNone', A_NS);
    const buCharEl = directChild(pPr, 'buChar', A_NS);
    const buAutoNum = !!directChild(pPr, 'buAutoNum', A_NS);
    const explicitBullet = buNone ? false : (buCharEl || buAutoNum ? true : undefined);
    const bullet =
      explicitBullet ??
      inherited?.bullet ??
      (bucketForPlaceholder(placeholderType) === 'body' && level >= 0);
    const bulletChar = buCharEl?.getAttribute('char') || inherited?.bulletChar;

    const runs: PptxTextRun[] = [];
    const inheritedRun = inherited?.run || {};
    for (const child of Array.from(p.children)) {
      if (child.namespaceURI !== A_NS) continue;
      if (child.localName === 'r' || child.localName === 'fld') {
        const tEl = directChild(child, 't', A_NS);
        const text = tEl?.textContent || '';
        const rPr = directChild(child, 'rPr', A_NS);
        const explicitRun = parseRunFormat(rPr, theme, clrMap);
        runs.push({ ...inheritedRun, ...explicitRun, text });
      } else if (child.localName === 'br') {
        runs.push({ ...inheritedRun, text: '\n' });
      }
    }

    out.push({
      runs,
      level,
      bullet,
      bulletChar,
      align,
    });
  }

  return out;
}

function guessMimeFromPath(path: string): string {
  const ext = (path.toLowerCase().split('.').pop() || '').trim();
  switch (ext) {
    case 'png':
      return 'image/png';
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'gif':
      return 'image/gif';
    case 'svg':
      return 'image/svg+xml';
    case 'webp':
      return 'image/webp';
    case 'bmp':
      return 'image/bmp';
    default:
      return 'application/octet-stream';
  }
}

/**
 * Parses a .pptx into a layout-faithful deck:
 * - true slide order from presentation.xml relationships
 * - theme color resolution (`schemeClr`, tint/shade/lum modifiers)
 * - master/layout placeholder inheritance (rect, padding, vertical align)
 * - master text defaults (title/body/other styles by level)
 * - embedded images as blob URLs
 */
export async function parsePptxDeck(arrayBuffer: ArrayBuffer): Promise<PptxDeck> {
  const zip = await JSZip.loadAsync(arrayBuffer);
  const parser = new DOMParser();
  const theme = await parseThemeDataAsync(zip, parser);

  let widthEmu = 9144000; // default 10"
  let heightEmu = 6858000; // default 7.5"
  const presXml = await zip.file('ppt/presentation.xml')?.async('string');
  const presRelsXml = await zip.file('ppt/_rels/presentation.xml.rels')?.async('string');

  const presentationDoc = presXml ? parser.parseFromString(presXml, 'application/xml') : null;
  if (presentationDoc) {
    const sldSz = presentationDoc.getElementsByTagNameNS(P_NS, 'sldSz')[0];
    if (sldSz) {
      widthEmu = parseInt(sldSz.getAttribute('cx') || '', 10) || widthEmu;
      heightEmu = parseInt(sldSz.getAttribute('cy') || '', 10) || heightEmu;
    }
  }

  const slidePaths: string[] = [];
  if (presentationDoc && presRelsXml) {
    const rels = parseRelationships(presRelsXml);
    const sldIds = presentationDoc.getElementsByTagNameNS(P_NS, 'sldId');
    for (let i = 0; i < sldIds.length; i++) {
      const rId = sldIds[i].getAttributeNS(R_NS, 'id') || sldIds[i].getAttribute('r:id');
      if (!rId || !rels[rId]) continue;
      if (!rels[rId].type.endsWith('/slide')) continue;
      slidePaths.push(resolveRelTarget('ppt/presentation.xml', rels[rId].target));
    }
  }
  if (slidePaths.length === 0) {
    slidePaths.push(
      ...Object.keys(zip.files)
        .filter((n) => /^ppt\/slides\/slide\d+\.xml$/i.test(n))
        .sort((a, b) => {
          const ai = parseInt(a.match(/slide(\d+)\.xml$/i)?.[1] ?? '0', 10);
          const bi = parseInt(b.match(/slide(\d+)\.xml$/i)?.[1] ?? '0', 10);
          return ai - bi;
        })
    );
  }

  const masterCache = new Map<string, MasterData>();
  const layoutCache = new Map<string, LayoutData>();

  const readMaster = async (masterPath: string): Promise<MasterData> => {
    const cached = masterCache.get(masterPath);
    if (cached) return cached;
    const xml = await zip.file(masterPath)?.async('string');
    if (!xml) {
      const fallback: MasterData = {
        clrMap: { ...DEFAULT_CLR_MAP },
        textStyles: EMPTY_TEXT_STYLES,
        placeholders: {},
      };
      masterCache.set(masterPath, fallback);
      return fallback;
    }
    const doc = parser.parseFromString(xml, 'application/xml');
    const clrMap = parseMasterClrMap(doc);
    const ctx: ParseCtx = { theme, clrMap };
    const cSld = doc.getElementsByTagNameNS(P_NS, 'cSld')[0];
    const out: MasterData = {
      clrMap,
      textStyles: parseMasterTextStyles(doc, ctx),
      placeholders: parsePlaceholderDefaults(cSld, theme, clrMap),
      background: parseBackgroundColor(cSld, theme, clrMap),
    };
    masterCache.set(masterPath, out);
    return out;
  };

  const readLayout = async (layoutPath: string): Promise<LayoutData> => {
    const cached = layoutCache.get(layoutPath);
    if (cached) return cached;

    const xml = await zip.file(layoutPath)?.async('string');
    if (!xml) {
      const fallback: LayoutData = { placeholders: {} };
      layoutCache.set(layoutPath, fallback);
      return fallback;
    }

    const doc = parser.parseFromString(xml, 'application/xml');
    const cSld = doc.getElementsByTagNameNS(P_NS, 'cSld')[0];
    const relsXml = await zip
      .file(`ppt/slideLayouts/_rels/${layoutPath.split('/').pop()}.rels`)
      ?.async('string');
    const rels = relsXml ? parseRelationships(relsXml) : {};
    const masterRel = Object.values(rels).find((r) => r.type.endsWith('/slideMaster'));
    const masterPath = masterRel ? resolveRelTarget(layoutPath, masterRel.target) : undefined;
    const master = masterPath ? await readMaster(masterPath) : undefined;
    const clrMap = master?.clrMap || { ...DEFAULT_CLR_MAP };
    const out: LayoutData = {
      masterPath,
      placeholders: parsePlaceholderDefaults(cSld, theme, clrMap),
      background: parseBackgroundColor(cSld, theme, clrMap),
    };
    layoutCache.set(layoutPath, out);
    return out;
  };

  const blobUrls: string[] = [];
  const slides: PptxSlideLayout[] = [];

  for (let i = 0; i < slidePaths.length; i++) {
    const slidePath = slidePaths[i];
    const slideXml = await zip.file(slidePath)?.async('string');
    if (!slideXml) continue;

    const slideDoc = parser.parseFromString(slideXml, 'application/xml');
    const slideRelsXml = await zip
      .file(`ppt/slides/_rels/${slidePath.split('/').pop()}.rels`)
      ?.async('string');
    const slideRels = slideRelsXml ? parseRelationships(slideRelsXml) : {};
    const layoutRel = Object.values(slideRels).find((r) => r.type.endsWith('/slideLayout'));
    const layoutPath = layoutRel ? resolveRelTarget(slidePath, layoutRel.target) : undefined;
    const layout = layoutPath ? await readLayout(layoutPath) : undefined;
    const master = layout?.masterPath ? await readMaster(layout.masterPath) : undefined;

    const clrMap = master?.clrMap || { ...DEFAULT_CLR_MAP };
    const cSld = slideDoc.getElementsByTagNameNS(P_NS, 'cSld')[0];
    const background =
      parseBackgroundColor(cSld, theme, clrMap) ||
      layout?.background ||
      master?.background ||
      '#ffffff';

    const spTree = cSld ? directChild(cSld, 'spTree', P_NS) : null;
    const shapes: PptxShape[] = [];

    if (spTree) {
      for (const node of Array.from(spTree.children)) {
        if (node.namespaceURI !== P_NS) continue;

        if (node.localName === 'sp') {
          const ph = getPlaceholderInfo(node);
          const layoutPh = ph ? layout?.placeholders[ph.key] : undefined;
          const masterPh = ph ? master?.placeholders[ph.key] : undefined;
          const inheritedPh = mergePlaceholderDefaults(layoutPh, masterPh);

          const spPr = directChild(node, 'spPr', P_NS);
          const txBody = directChild(node, 'txBody', P_NS);
          const bodyPr = directChild(txBody, 'bodyPr', A_NS);

          const rect = parseXfrmRect(spPr) || inheritedPh.rect || null;
          if (!rect) continue;

          const fill = parseShapeFill(node, spPr, theme, clrMap) ?? inheritedPh.fill;
          const padding =
            parseBodyPadding(bodyPr) ||
            inheritedPh.padding || {
              left: 9.6,
              right: 9.6,
              top: 4.8,
              bottom: 4.8,
            };
          const verticalAlign =
            parseVerticalAlign(bodyPr) || inheritedPh.verticalAlign || 'top';

          const bucket = master?.textStyles[bucketForPlaceholder(ph?.type || null)] || [];
          const firstDefault = bucket[0]?.run || {};
          const defaultFontSizePt =
            firstDefault.fontSizePt || defaultFontSizeForPh(ph?.type || null);
          const defaultTextColor = firstDefault.color;
          const defaultFontFamily = firstDefault.fontFamily;

          const paragraphs = txBody
            ? parseParagraphs(
                txBody,
                ph?.type || null,
                master?.textStyles || EMPTY_TEXT_STYLES,
                theme,
                clrMap
              )
            : [];

          shapes.push({
            kind: 'text',
            rect,
            paragraphs,
            fill,
            defaultFontSizePt,
            defaultTextColor,
            defaultFontFamily,
            padding,
            verticalAlign,
          });
        } else if (node.localName === 'pic') {
          const spPr = directChild(node, 'spPr', P_NS);
          const rect = parseXfrmRect(spPr);
          if (!rect) continue;

          const blipFill = directChild(node, 'blipFill', P_NS);
          const blip = directChild(blipFill, 'blip', A_NS);
          const embedId =
            blip?.getAttributeNS(R_NS, 'embed') || blip?.getAttribute('r:embed') || '';
          const rel = embedId ? slideRels[embedId] : undefined;
          if (!rel) continue;

          const mediaPath = resolveRelTarget(slidePath, rel.target);
          const mediaEntry = zip.file(mediaPath);
          if (!mediaEntry) continue;
          const raw = await mediaEntry.async('blob');
          const typed = new Blob([raw], { type: guessMimeFromPath(mediaPath) });
          const src = URL.createObjectURL(typed);
          blobUrls.push(src);
          shapes.push({ kind: 'image', rect, src });
        }
      }
    }

    slides.push({ index: i + 1, shapes, background });
  }

  return {
    width: widthEmu / EMU_PER_PX,
    height: heightEmu / EMU_PER_PX,
    slides,
    blobUrls,
  };
}

// ---------- PPTX (back-compat text) ----------

/** Back-compat: flatten pptx to a text string (used by the send path). */
export const readPptxFile = async (file: File): Promise<string> => {
  try {
    const slides = await parsePptxBuffer(await readFileAsArrayBuffer(file));
    return slides
      .map((s) => `--- Slide ${s.index} ---\n${s.paragraphs.join('\n')}`.trimEnd())
      .join('\n\n');
  } catch (error: any) {
    throw new Error(
      error?.message
        ? `${error.message} (file: ${file.name}, size: ${file.size})`
        : `Failed to read PPTX (file: ${file.name})`
    );
  }
};

// ---------- XLSX ----------

export interface XlsxSheet {
  name: string;
  rows: string[][];
}

const colIndexFromRef = (ref: string): number => {
  const letters = ref.replace(/[^A-Z]/gi, '').toUpperCase();
  let idx = 0;
  for (let i = 0; i < letters.length; i++) {
    idx = idx * 26 + (letters.charCodeAt(i) - 64);
  }
  return Math.max(0, idx - 1);
};

/**
 * Parses a .xlsx (OpenXML SpreadsheetML) ArrayBuffer into a list of sheets,
 * each a 2D array of cell values. Shared strings and booleans are resolved.
 * Formulas are represented by their cached computed value (the `<v>` child)
 * rather than the formula text.
 */
export async function parseXlsxBuffer(arrayBuffer: ArrayBuffer): Promise<XlsxSheet[]> {
  const zip = await JSZip.loadAsync(arrayBuffer);
  const parser = new DOMParser();
  const ssNs = 'http://schemas.openxmlformats.org/spreadsheetml/2006/main';

  const sharedStrings: string[] = [];
  const sharedStringsXml = await zip.file('xl/sharedStrings.xml')?.async('string');
  if (sharedStringsXml) {
    const ssDoc = parser.parseFromString(sharedStringsXml, 'application/xml');
    const siEls = ssDoc.getElementsByTagNameNS(ssNs, 'si');
    for (let i = 0; i < siEls.length; i++) {
      const tEls = siEls[i].getElementsByTagNameNS(ssNs, 't');
      let combined = '';
      for (let t = 0; t < tEls.length; t++) combined += tEls[t].textContent || '';
      sharedStrings.push(combined);
    }
  }

  const workbookXml = await zip.file('xl/workbook.xml')?.async('string');
  const workbookRelsXml = await zip.file('xl/_rels/workbook.xml.rels')?.async('string');
  if (!workbookXml || !workbookRelsXml) {
    throw new Error('XLSX file missing workbook metadata');
  }
  const wbDoc = parser.parseFromString(workbookXml, 'application/xml');
  const relsDoc = parser.parseFromString(workbookRelsXml, 'application/xml');

  const relTargetById: Record<string, string> = {};
  const relationships = relsDoc.getElementsByTagName('Relationship');
  for (let i = 0; i < relationships.length; i++) {
    const id = relationships[i].getAttribute('Id');
    const target = relationships[i].getAttribute('Target');
    if (id && target) relTargetById[id] = target;
  }

  const sheetEls = wbDoc.getElementsByTagNameNS(ssNs, 'sheet');
  const sheetMeta: { name: string; path: string }[] = [];
  for (let i = 0; i < sheetEls.length; i++) {
    const name = sheetEls[i].getAttribute('name') || `Sheet${i + 1}`;
    const rId =
      sheetEls[i].getAttributeNS(
        'http://schemas.openxmlformats.org/officeDocument/2006/relationships',
        'id'
      ) || sheetEls[i].getAttribute('r:id');
    const rel = rId ? relTargetById[rId] : undefined;
    if (!rel) continue;
    const path = rel.startsWith('/') ? rel.slice(1) : `xl/${rel.replace(/^xl\//, '')}`;
    sheetMeta.push({ name, path });
  }
  if (sheetMeta.length === 0) throw new Error('XLSX file has no sheets');

  const sheets: XlsxSheet[] = [];
  for (const sheet of sheetMeta) {
    const sheetXml = await zip.file(sheet.path)?.async('string');
    if (!sheetXml) continue;
    const sheetDoc = parser.parseFromString(sheetXml, 'application/xml');
    const rowEls = sheetDoc.getElementsByTagNameNS(ssNs, 'row');
    const rows: string[][] = [];
    for (let r = 0; r < rowEls.length; r++) {
      const row = rowEls[r];
      const cellEls = row.getElementsByTagNameNS(ssNs, 'c');
      const rowCells: string[] = [];
      for (let c = 0; c < cellEls.length; c++) {
        const cell = cellEls[c];
        const ref = cell.getAttribute('r') || '';
        const typeAttr = cell.getAttribute('t');
        const col = ref ? colIndexFromRef(ref) : rowCells.length;

        let value = '';
        if (typeAttr === 'inlineStr') {
          const tEls = cell.getElementsByTagNameNS(ssNs, 't');
          for (let t = 0; t < tEls.length; t++) value += tEls[t].textContent || '';
        } else {
          const vEl = cell.getElementsByTagNameNS(ssNs, 'v')[0];
          const raw = vEl?.textContent ?? '';
          if (typeAttr === 's') {
            const idx = parseInt(raw, 10);
            value = Number.isFinite(idx) ? (sharedStrings[idx] ?? '') : '';
          } else if (typeAttr === 'b') {
            value = raw === '1' ? 'TRUE' : 'FALSE';
          } else {
            value = raw;
          }
        }

        while (rowCells.length < col) rowCells.push('');
        rowCells[col] = value;
      }
      rows.push(rowCells);
    }
    sheets.push({ name: sheet.name, rows });
  }
  return sheets;
}

/** Back-compat: flatten xlsx to a text (TSV) blob (used by the send path). */
export const readXlsxFile = async (file: File): Promise<string> => {
  try {
    const sheets = await parseXlsxBuffer(await readFileAsArrayBuffer(file));
    return sheets
      .map(
        (s) =>
          `--- Sheet: ${s.name} ---\n` +
          s.rows.map((r) => r.map((v) => v.replace(/\t/g, ' ')).join('\t')).join('\n')
      )
      .join('\n\n');
  } catch (error: any) {
    throw new Error(
      error?.message
        ? `${error.message} (file: ${file.name}, size: ${file.size})`
        : `Failed to read XLSX (file: ${file.name})`
    );
  }
};
